import { constant, compose } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getStringOrUndefinedProp, getStringProp } from '@mews/fp';
import { DateTimeUtcService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { isDefiniteState } from '@mews-commander/types';
import { ManualOrigin, ServiceOrderState } from '@mews-commander/legacy-data-structures';
import { oneOf, createEnumOptionsSelector } from '@mews-commander/utils';
import { dateString, email, presence, timeString, withinDateTimeRange } from '@mews-commander/legacy-form';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { moduleSelector } from '../module';
import { INITIAL_STATE } from '../reducers/options';
import MailReceivers from '../enums/MailReceivers';

const BASE_CONSTRAINTS = {
    customerId: { presence },
    origin: { presence },
};

const CONFIRMATION_CONSTRAINTS = {
    confirmationEmail: { email },
};

const getOptions = createSelector(
    moduleSelector,
    getStringProp('scope'),
    (state, scope) => state.options[scope] || INITIAL_STATE
);

export const getOptionValues = createSelector(getOptions, (options) => options.values || {});
export const getOptionErrors = createSelector(getOptions, (options) => options.errors || {});
export const getTouchedOptions = createSelector(getOptions, (options) => options.touched || {});
export const areAllOptionsTouched = createSelector(getOptions, (options) => !!options.allTouched);

export const getReservationGroupId = createSelector(getOptionValues, (options) => options.reservationGroupId);
export const getCustomerId = createSelector(getOptionValues, (options) => options.customerId);
export const getBookerId = createSelector(getOptionValues, (options) => options.bookerId);
export const getPartnerCompanyId = createSelector(getOptionValues, (options) => options.partnerCompanyId);
export const getTravelAgencyId = createSelector(getOptionValues, (options) => options.travelAgencyId);
export const getOrigin = createSelector(getOptionValues, (options) => options.origin);
export const getState = createSelector(getOptionValues, (options) => options.state || ServiceOrderState.Confirmed.name);
export const getNotes = createSelector(getOptionValues, (options) => options.notes);
export const getReleaseDate = createSelector(getOptionValues, (options) => options.releaseDate);
export const getReleaseTime = createSelector(getOptionValues, (options) => options.releaseTime);
export const getConfirmationEmailReceivers = createSelector(
    getOptionValues,
    (options) => options.confirmationEmailReceivers || MailReceivers.None.value
);
export const getConfirmationLanguageCode = createSelector(
    getOptionValues,
    (options) => options.confirmationLanguageCode
);

export const isTravelAgencySelected = createSelector(getTravelAgencyId, (travelAgencyId) => !!travelAgencyId);

export const isDefinite = compose(isDefiniteState, getState);

export const getConfirmationLabel = createSelector(isDefinite, (definite) =>
    definite ? TextKey.Confirmation : TextKey.Quotation
);

export const getServiceOrderStateOptions = createEnumOptionsSelector(
    constant([ServiceOrderState.Inquired, ServiceOrderState.Optional, ServiceOrderState.Confirmed]),
    { useNameAsValue: true }
);

const getConfirnationEmailReceiversEnumValue = createSelector(getConfirmationEmailReceivers, (receivers: string) =>
    MailReceivers.getByValue(receivers)
);
export const shouldSendConfirmation = createSelector(getConfirnationEmailReceiversEnumValue, (v) =>
    oneOf(v, MailReceivers.CustomerAndBooker, MailReceivers.Booker)
);

const getReleaseConstraints = ({
    nowUtc,
    reservationMinArrivalUtc,
    releaseDate,
    releaseTime,
    timezone,
    key,
}: $TSFixMe) => {
    const baseReleaseConstraints = {
        presence,
        withinDateTimeRange: {
            ...withinDateTimeRange,
            startUtc: nowUtc,
            endUtc: reservationMinArrivalUtc,
            dateStr: releaseDate,
            timeStr: releaseTime,
            timezone,
            key: key === 'releaseDate' ? 'date' : 'time',
        },
    };
    return {
        releaseDate: {
            dateString,
            ...baseReleaseConstraints,
        },
        releaseTime: {
            timeString,
            ...baseReleaseConstraints,
        },
    };
};

export const getOptionConstraints = createSelector(
    shouldSendConfirmation,
    getStringOrUndefinedProp('reservationMinArrivalUtc'),
    getStringProp('key'),
    getReleaseDate,
    getReleaseTime,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    isDefinite,
    (sendConfirmation, reservationMinArrivalUtc, key, releaseDate, releaseTime, timezone, isStateDefinite) => {
        const baseConstraints = {
            ...BASE_CONSTRAINTS,
            ...(sendConfirmation ? CONFIRMATION_CONSTRAINTS : {}),
        };

        if (!isStateDefinite) {
            const nowUtc = DateTimeUtcService.now();
            const releaseConstraints = getReleaseConstraints({
                nowUtc,
                reservationMinArrivalUtc,
                releaseDate,
                releaseTime,
                timezone,
                key,
            });

            return {
                ...baseConstraints,
                ...releaseConstraints,
            };
        }

        return baseConstraints;
    }
);

export const createOptionErrorSelector = (key: $TSFixMe) =>
    createSelector(
        areAllOptionsTouched,
        getTouchedOptions,
        getOptionErrors,
        (allTouched, touched, errors) => (allTouched || touched[key]) && errors[key]
    );

// @ts-expect-error $TSFixMeError: Expected 2-13 arguments, but got 1.
export const getAvailableOrigins = createSelector(() => ManualOrigin.enumValues);
